import {
    IAuthorPage,
    ICategoryInfosAndPages,
    ILatestPost,
    IWPCardPost,
    IWPCommentsPayload,
    IWPContentPayload,
    IWPIdBySlug,
    IWPSitemapSlug,
    TBreadcrumb,
} from "@/app/models/wp"
import type { IYoastSEOMeta } from "@/app/models/yoast"
import * as Sentry from '@sentry/nextjs'
import { notFound } from "next/navigation"
export const BASE_API: string = `${process.env.NEXT_PUBLIC_RM_BASE_AUCTOR_API}/api`
export const WP_API: string = `${process.env.NEXT_PUBLIC_RM_WORDPRESS_API}/guide/wp-json/rm-api`
export const WP_API_V2: string = `${process.env.NEXT_PUBLIC_RM_WORDPRESS_API}/guide/wp-json/rm-api-v2`
export const YOAST_API: string = `${process.env.NEXT_PUBLIC_RM_WORDPRESS_API}/guide/wp-json/wp/v2`
export const WP_PATH_PREFIX = "/guide"

export const WP_CACHE_POSTS_ARTICLES = "WP_CACHE_POSTS_ARTICLES"
export const WP_CACHE_COMMENTS = "WP_CACHE_COMMENTS"
export const WP_CACHE_AUTHOR = "WP_CACHE_AUTHOR"
export const WP_CACHE_CONTENT = "WP_CACHE_CONTENT"
export const WP_CACHE_META = "WP_CACHE_META"
export const WP_CACHE_OTHER = "WP_CACHE_OTHER"
export const WP_CACHE_NEWS_HOMEPAGE = "WP_CACHE_NEWS_HOMEPAGE"

export const ENDPOINTS = {
    WP_CACHE_POSTS_ARTICLES: {
        GET_CATEGORY_INFOS_AND_PAGES: `${WP_API_V2}/get-category-infos-and-pages-list-by-id?id=%CATEGORY_ID%`,
        GET_NEIGHBORS_POSTS: `${WP_API_V2}/get-neighbors-posts?pageId=%POST_ID%&postsNumber=%POSTS_NUMBER%`,
    },

    WP_CACHE_CONTENT: {
        GET_WP_BY_ID: `${WP_API_V2}/get-page-by-id?pageId=%ID%`,
        GET_ID_BY_SLUG: `${WP_API_V2}/get-page-id-by-slug?slug=%SLUG%`,
    },

    WP_CACHE_COMMENTS: {
        GET_PAGE_COMMENTS: `${WP_API_V2}/get-page-comments-by-id?pageId=%ID%`,
        POST_NEW_COMMENT: `${BASE_API}/wp/page/comment/for-id`,
        TOGGLE_COMMENT_LIKE: `${WP_API_V2}/toggle-comment-like`,
    },

    WP_CACHE_AUTHOR: {
        GET_AUTHOR_BY_NAME: `${WP_API_V2}/get-author-by-name?`,
    },

    WP_CACHE_META: {
        GET_BREADCRUMB_BY_ID: `${WP_API_V2}/get-breadcrumb-by-id-from-topic?pageId=%ID%`,
        GET_YOAST_META_BY_ID: `${WP_API_V2}/get-meta-by-id?pageId=%ID%`,
        GET_YOAST_CATEGORY_META_BY_ID: `${YOAST_API}/categories/%ID%`,
    },

    WP_CACHE_NEWS_HOMEPAGE: {
        GET_LAST_POSTS: `${WP_API_V2}/get-latest-pages?limit=%LIMIT%`,
    },

    WP_CACHE_OTHER: {
        GET_REDIRECTIONS: `${WP_API_V2}/get-wp-redirections`,
        GET_WP_SLUGS: `${WP_API_V2}/slugs`,
        GET_WP_SLUGS_FOR_SITEMAP: `${WP_API_V2}/slugs-for-sitemap`,
    },
}

export const WP_CACHE_TAGS_TO_DESCRIPTION = [
    {
        tag: WP_CACHE_POSTS_ARTICLES,
        description: "Catégories et articles similaires",
        urls: [...Object.values(ENDPOINTS.WP_CACHE_POSTS_ARTICLES)]
    },
    {
        tag: WP_CACHE_CONTENT,
        description: "Contenu des articles et posts",
        urls: [...Object.values(ENDPOINTS.WP_CACHE_CONTENT)]
    },
    {
        tag: WP_CACHE_COMMENTS,
        description: "Commentaires",
        urls: [...Object.values(ENDPOINTS.WP_CACHE_COMMENTS)]
    },
    {
        tag: WP_CACHE_AUTHOR,
        description: "Auteurs",
        urls: [...Object.values(ENDPOINTS.WP_CACHE_AUTHOR)]
    },
    {
        tag: WP_CACHE_META,
        description: "Méta (breadcrumb, schema)",
        urls: [...Object.values(ENDPOINTS.WP_CACHE_META)]
    },
    {
        tag: WP_CACHE_NEWS_HOMEPAGE,
        description: "Cache des derniers articles de la homepage",
        urls: [...Object.values(ENDPOINTS.WP_CACHE_NEWS_HOMEPAGE)]
    },
    {
        tag: WP_CACHE_OTHER,
        description: "Autres (redirections, sitemap, prettylink)",
        urls: [...Object.values(ENDPOINTS.WP_CACHE_OTHER)]
    },
]

type ErrorMode = 'throw' | 'notFound' | 'silent'

type WpFetchCacheOptions = {
    cache: RequestCache
    next: {tags: string[]; revalidate?: number}
}

// Hors prod, 'no-store' donnait du contenu frais aux rédacteurs mais interdit tout rendu
// statique : une route ISR lève DYNAMIC_SERVER_USAGE dès le premier rendu. Une revalidation
// courte offre la même fraîcheur sans empêcher la mise en cache de la page.
const WP_REVALIDATE_OUTSIDE_PROD = 30

function buildCacheOptions(cache: boolean, cacheTag: string, revalidate?: number): WpFetchCacheOptions {
    if (!cache) {
        return {cache: 'no-store', next: {tags: [cacheTag]}}
    }

    const isProd = process.env.NEXT_PUBLIC_APP_ENV === 'prod'

    return {
        cache: 'force-cache',
        next: {tags: [cacheTag], revalidate: revalidate ?? (isProd ? undefined : WP_REVALIDATE_OUTSIDE_PROD)},
    }
}

// WordPress stalls under the prerender burst, so a transient failure is retried before it fails a whole page
const WP_TIMEOUT_MS = 10000
const WP_MAX_ATTEMPTS = 3
const WP_RETRY_DELAY_MS = 500

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const isTransientStatus = (status: number) => status >= 500

// A fresh timeout signal per attempt: an aborted signal would abort every later attempt at once
export async function fetchWpWithRetry(url: string, init: RequestInit): Promise<Response> {
    for (let attempt = 1; ; attempt++) {
        const isLastAttempt = attempt === WP_MAX_ATTEMPTS
        try {
            const response = await fetch(url, {...init, signal: AbortSignal.timeout(WP_TIMEOUT_MS)})
            if (isLastAttempt || !isTransientStatus(response.status)) return response
            console.warn(`WP API ${response.status} at ${url}, retrying (${attempt}/${WP_MAX_ATTEMPTS})`)
        } catch (error) {
            if (isLastAttempt) throw error
            console.warn(`WP API ${(error as Error).name} at ${url}, retrying (${attempt}/${WP_MAX_ATTEMPTS})`)
        }
        await wait(WP_RETRY_DELAY_MS * attempt)
    }
}

export async function hitWpApi(
    url: string,
    {
        cache = true,
        cacheTag = WP_CACHE_OTHER,
        onError = 'throw',
        revalidate,
    }: {
        cache?: boolean
        cacheTag?: string
        onError?: ErrorMode
        // Seconds before the cached response is refetched; overrides the env default (never in prod, 30s elsewhere)
        revalidate?: number
    } = {}
): Promise<Response | null> {
    let response: Response
    try {
        response = await fetchWpWithRetry(url, {
            ...buildCacheOptions(cache, cacheTag, revalidate),
            headers: {
                'X-Guide-Proxy-Key': process.env.WP_PROXY_KEY || 'FUbXPtxQYy9sWp97OGMammOnjiDIfNBe',
            },
        })
    } catch (error) {
        if (onError === 'silent') {
            Sentry.captureMessage(`Silent WP API network error at ${url}: ${(error as Error).message}`, 'warning')
            return null
        }
        Sentry.captureException(error, {tags: {source: 'hitWpApi'}, extra: {url}})
        throw error
    }

    // in this function we're avoiding try/catch here to allow next to catch the notFound error
    // and display the custom 404 page
    if (response.ok) return response

    if (onError === 'notFound' && response.status === 404) {
        console.error(`WP API error 404 at ${url}`)
        notFound()
    }

    if (onError === 'silent') {
        Sentry.captureMessage(`Silent WP API error ${response.status} at ${url}`, 'warning')
        return null
    }

    const error = new Error(`WP API error ${response.status} at ${url}`)
    Sentry.captureException(error, {
        tags: {source: 'hitWpApi'},
        extra: {url, status: response.status},
    })

    throw error
}

export async function safeWpJson(
    url: string,
    options?: Parameters<typeof hitWpApi>[1],
    fallback: any = []
): Promise<any> {
    const res = await hitWpApi(url, options)
    if (!res) return fallback

    try {
        return await res.json()
    } catch (e) {
        console.error(`Failed to parse JSON from ${url}`, e)
        Sentry.captureException(e, {
            tags: {source: 'hitWpApi'},
            extra: {url, status: res.status},
        })


        if (options?.onError === 'silent') {
            return fallback
        }

        throw new Error(`Failed to parse JSON from ${url}`)
    }
}

export async function getLastPosts(limit: number = 10): Promise<ILatestPost[]> {
    const url = ENDPOINTS.WP_CACHE_NEWS_HOMEPAGE.GET_LAST_POSTS.replace('%LIMIT%', limit.toString())

    return await safeWpJson(url, {cache: true, cacheTag: WP_CACHE_NEWS_HOMEPAGE, onError: 'silent'})
}

// slugs-for-sitemap walks every WP post uncached (~3s warm, 502 cold on preprod), so it must not follow the 30s default
const WP_SLUGS_FOR_SITEMAP_REVALIDATE = 86400

export async function getWpSlugsForSitemap(): Promise<IWPSitemapSlug[]> {
    return await safeWpJson(
        ENDPOINTS.WP_CACHE_OTHER.GET_WP_SLUGS_FOR_SITEMAP,
        {cache: true, cacheTag: WP_CACHE_OTHER, onError: 'silent', revalidate: WP_SLUGS_FOR_SITEMAP_REVALIDATE},
        []
    )
}

export async function getCategoryInfosAndPagesById(categoryId: string, cache: boolean = true): Promise<ICategoryInfosAndPages> {
    const url = ENDPOINTS.WP_CACHE_POSTS_ARTICLES.GET_CATEGORY_INFOS_AND_PAGES.replace('%CATEGORY_ID%', String(categoryId))

    return await safeWpJson(url, {cache, cacheTag: WP_CACHE_POSTS_ARTICLES, onError: 'notFound'})
}

export async function getWPIdBySlug(slug: string, cache = true): Promise<IWPIdBySlug> {
    const url = ENDPOINTS.WP_CACHE_CONTENT.GET_ID_BY_SLUG.replace("%SLUG%", slug)

    return await safeWpJson(url, {cache, cacheTag: WP_CACHE_CONTENT, onError: 'notFound'})
}

export async function getWPContent(id: string, cache = true, preview = false): Promise<IWPContentPayload> {
    let url = ENDPOINTS.WP_CACHE_CONTENT.GET_WP_BY_ID.replace("%ID%", id)

    if (preview) {
        url += "&preview=true&token=" + process.env.RM_WP_PREVIEW_SECRET
        cache = false
    }

    return await safeWpJson(url, {cache, cacheTag: WP_CACHE_CONTENT, onError: 'notFound'})
}

export async function getWPPageComments(id: string, commentsNumber?: number, cache = true): Promise<IWPCommentsPayload> {
    let url = ENDPOINTS.WP_CACHE_COMMENTS.GET_PAGE_COMMENTS.replace("%ID%", id)

    if (commentsNumber) {
        url += `&number=${commentsNumber}`
    }

    return await safeWpJson(url, {cache, cacheTag: WP_CACHE_COMMENTS, onError: 'silent'})
}

export async function getNeighborsPosts(wpPageId: string, postsNumber: string, cache = true): Promise<IWPCardPost[]> {
    const url = ENDPOINTS.WP_CACHE_POSTS_ARTICLES.GET_NEIGHBORS_POSTS.replace("%POST_ID%", wpPageId).replace("%POSTS_NUMBER%", postsNumber)

    return await safeWpJson(url, {cache, cacheTag: WP_CACHE_POSTS_ARTICLES, onError: 'silent'})
}

export async function getAuthorByName(firstName?: string, lastName?: string, cache = true): Promise<IAuthorPage> {
    let url = ENDPOINTS.WP_CACHE_AUTHOR.GET_AUTHOR_BY_NAME
    if (firstName) {
        url = url + "first_name=" + firstName
    }

    if (lastName) {
        if (firstName) {
            url = url + "&"
        }

        url = url + "last_name=" + lastName
    }

    return await safeWpJson(url, {cache, cacheTag: WP_CACHE_AUTHOR, onError: 'silent'})
}

export async function getBreadcrumbByID(id: string | number, cache = true): Promise<TBreadcrumb> {
    id = String(id)
    const url = ENDPOINTS.WP_CACHE_META.GET_BREADCRUMB_BY_ID.replace("%ID%", id)

    return await safeWpJson(url, {cache, cacheTag: WP_CACHE_META, onError: 'silent'})
}

export async function getYoastMetaByID(id: string, cache = true): Promise<IYoastSEOMeta> {
    const url = ENDPOINTS.WP_CACHE_META.GET_YOAST_META_BY_ID.replace("%ID%", id)

    return await safeWpJson(url, {cache, cacheTag: WP_CACHE_META, onError: 'silent'})
}

export async function getYoastCategoryMetaByID(id: string, cache: boolean = true): Promise<any> {
    const url = ENDPOINTS.WP_CACHE_META.GET_YOAST_CATEGORY_META_BY_ID.replace('%ID%', id)

    return await safeWpJson(url, {cache, cacheTag: WP_CACHE_META, onError: 'silent'})
}

export async function replacePostSummary(postId: number, content: string): Promise<{success: boolean, message?: string}> {
    const url = `${process.env.NEXT_PUBLIC_RM_WORDPRESS_API}/guide/wp-json/rm-api/replace-post-summary`

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Guide-Proxy-Key': process.env.WP_PROXY_KEY || 'FUbXPtxQYy9sWp97OGMammOnjiDIfNBe',
            },
            body: JSON.stringify({
                post_id: postId,
                content: content
            })
        })

        if (!response.ok) {
            throw new Error(`HTTP error ${response.status}`)
        }

        return {
            success: true,
            message: 'Contenu mis à jour avec succès'
        }
    } catch (error: any) {
        return {
            success: false,
            message: error?.message || 'Erreur inconnue'
        }
    }
}

export async function previewPostSummary(postId: number, content: string): Promise<{success: boolean, modified_content?: any, message?: string}> {
    const url = `${process.env.NEXT_PUBLIC_RM_WORDPRESS_API}/guide/wp-json/rm-api/preview-post-summary`

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Guide-Proxy-Key': process.env.WP_PROXY_KEY || 'FUbXPtxQYy9sWp97OGMammOnjiDIfNBe',
            },
            body: JSON.stringify({
                post_id: postId,
                content: content
            })
        })

        if (!response.ok) {
            throw new Error(`HTTP error ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error: any) {
        return {
            success: false,
            message: error?.message || 'Erreur inconnue'
        }
    }
}

export async function generateCommentTitles(slug: string): Promise<{
    success: boolean,
    message?: string,
    post_id?: string,
    post_title?: string,
    total_comments?: number,
    comments_without_titles?: number,
    processed?: number,
    errors?: number,
    results?: Array<{
        comment_id: string,
        success: boolean,
        error?: string | null,
        title?: string,
        comment_author?: string,
        comment_excerpt?: string
    }>
}> {
    const url = `${process.env.NEXT_PUBLIC_RM_WORDPRESS_API}/guide/wp-json/rm-api-v2/generate-comment-titles`

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Guide-Proxy-Key': process.env.WP_PROXY_KEY || 'FUbXPtxQYy9sWp97OGMammOnjiDIfNBe',
            },
            body: JSON.stringify({
                slug: slug
            })
        })

        if (!response.ok) {
            throw new Error(`HTTP error ${response.status}`)
        }

        const data = await response.json()
        return {
            success: true,
            ...data
        }
    } catch (error: any) {
        return {
            success: false,
            message: error?.message || 'Erreur inconnue'
        }
    }
}

export async function toggleCommentLike(commentId: string): Promise<{
    success: boolean,
    message?: string,
    isLiked?: boolean,
    totalLikes?: number
}> {
    const url = ENDPOINTS.WP_CACHE_COMMENTS.TOGGLE_COMMENT_LIKE

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Guide-Proxy-Key': process.env.WP_PROXY_KEY || 'FUbXPtxQYy9sWp97OGMammOnjiDIfNBe',
            },
            body: JSON.stringify({
                commentId: commentId
            })
        })

        if (!response.ok) {
            throw new Error(`HTTP error ${response.status}`)
        }

        const data = await response.json()
        return {
            success: true,
            ...data
        }
    } catch (error: any) {
        return {
            success: false,
            message: error?.message || 'Erreur inconnue'
        }
    }
}

